import React, { useState, useRef } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { patch } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';

// Menu tab module: expects full website object
function Menu({ website, onUpdated }) {
  const hasWebsite = !!website;
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  // items now support nested children up to depth 2
  const [items, setItems] = useState((website && (website?.menu?.items || [])) || []);
  // Drag & drop across levels uses path arrays instead of flat indices
  const [dragPath, setDragPath] = useState(null); // e.g. [1, 2]
  const [overPath, setOverPath] = useState(null);
  const [overPos, setOverPos] = useState(null); // 'before' | 'after' (same-level only)
  // map of pathKey => element ref
  const itemRefs = useRef({});

  const startEdit = () => {
    const deepClone = (arr) => (arr || []).map(it => ({ ...it, children: it.children ? deepClone(it.children) : [] }));
    setItems(deepClone(website?.menu?.items || []));
    setEdit(true);
  };

  const cancelEdit = () => {
    setEdit(false);
  };

  const addItem = () => {
    setItems(prev => [...prev, { slug: '', name: '', children: [] }]);
  };

  // Helpers to work with nested paths
  const getByPath = (list, path) => {
    let node = { children: list };
    for (let i = 0; i < path.length; i++) {
      const idx = path[i];
      node = (node.children || [])[idx];
      if (!node) return undefined;
    }
    return node;
  };

  const setByPath = (list, path, updater) => {
    const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
    const next = clone(list);
    let parent = { children: next };
    let node = parent;
    for (let i = 0; i < path.length; i++) {
      node = (node.children || [])[path[i]];
    }
    const updated = updater(node);
    // write back
    let p = { children: next };
    for (let i = 0; i < path.length - 1; i++) {
      p = p.children[path[i]];
    }
    if (path.length === 0) return updated.children || [];
    p.children[path[path.length - 1]] = updated;
    return next;
  };

  const removeByPath = (list, path) => {
    const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
    const next = clone(list);
    let parent = { children: next };
    for (let i = 0; i < path.length - 1; i++) {
      parent = parent.children[path[i]];
    }
    const idx = path[path.length - 1];
    const [removed] = (parent.children || []).splice(idx, 1);
    return [removed, parent.children ? next : (removed ? [] : next)];
  };

  const insertAtPath = (list, path, pos, node) => {
    const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
    const next = clone(list);
    if (pos === 'inside') {
      const target = getByPath(next, path);
      if (target) {
        if (!Array.isArray(target.children)) target.children = [];
        target.children.push(node);
      }
    } else {
      // before/after at same parent level
      let parent = { children: next };
      for (let i = 0; i < path.length - 1; i++) parent = parent.children[path[i]];
      const idx = path[path.length - 1] + (pos === 'after' ? 1 : 0);
      (parent.children || []).splice(idx, 0, node);
    }
    return next;
  };

  const pathKey = (path) => (path || []).join('.');

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Helper to slugify a title into a URL-safe path segment
  const slugify = (str) => {
    return (str || '')
      .toString()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '') // remove diacritics
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, '-') // spaces/underscores -> dash
      .replace(/[^a-z0-9-]/g, '') // remove invalid chars
      .replace(/-+/g, '-') // collapse dashes
      .replace(/^-+|-+$/g, ''); // trim dashes
  };

  // Given a parentSlug (full path or ''), build the full slug for a new name
  const buildFullSlugFromName = (parentSlug, name) => {
    const seg = slugify(name);
    if (!seg) return (parentSlug || '').replace(/^\/+|\/+$/g, '');
    const ps = (parentSlug || '').replace(/^\/+|\/+$/g, '');
    return ps ? `${ps}/${seg}` : seg;
  };

  // Update a nested field using path array
  const updateFieldAtPath = (path, field, value) => {
    setItems(prev => {
      const next = (arr => (arr || []).map(it => ({ ...it, children: (it.children || []).map(ch => ch) })))(prev);
      const node = getByPath(next, path);
      if (!node) return prev;
      const parent = path.length ? getByPath(next, path.slice(0, -1)) : null;
      if (field === 'name') {
        const newName = value;
        const oldName = node.name || '';
        const currentSlug = (node.slug || '').trim();
        const parentSlug = parent ? (parent.slug || '') : '';
        const baselineFromOld = buildFullSlugFromName(parentSlug, oldName);
        const shouldAuto = !currentSlug || currentSlug === baselineFromOld;
        node.name = newName;
        if (shouldAuto) node.slug = buildFullSlugFromName(parentSlug, newName);
      } else if (field === 'slug') {
        node.slug = (value || '').replace(/^\/+|\/+$/g, ''); // normalize, no leading/trailing slash
      } else {
        node[field] = value;
      }
      return [...next];
    });
  };

  // Note: DnD restricted to same-level sorting only. No cross-level drops.

  // Drag & Drop — same-level sorting only
  const onDragStart = (path, e) => {
    setDragPath(path);
    setOverPath(path);
    try {
      e.dataTransfer.setData('text/plain', path.join('.'));
    } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    // Make the full card follow the cursor
    const el = itemRefs.current[pathKey(path)];
    if (el && typeof e.dataTransfer.setDragImage === 'function') {
      e.dataTransfer.setDragImage(el, 16, 16);
    }
  };

  const onDragEnter = (path) => {
    if (!dragPath) return;
    const sameParent = dragPath.length === path.length && dragPath.slice(0, -1).every((v, i) => v === path[i]);
    if (!sameParent) return;
    if (!overPath || pathKey(path) !== pathKey(overPath)) setOverPath(path);
  };

  const onDragOver = (path, depth, e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Allow drop only for same parent level; decide before/after based on cursor Y
    if (!dragPath) return;
    // same-parent check
    const sameParent = dragPath.length === path.length && dragPath.slice(0, -1).every((v, i) => v === path[i]);
    if (!sameParent) {
      setOverPath(null);
      setOverPos(null);
      return;
    }
    const el = itemRefs.current[pathKey(path)];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const ratio = y / rect.height;
    const pos = ratio < 0.5 ? 'before' : 'after';
    setOverPath(path);
    setOverPos(pos);
  };

  const onDrop = (path, depth, e) => {
    e.preventDefault();
    if (!dragPath || !Array.isArray(dragPath)) return;
    // Only allow same-level sorting within the same parent
    const sameParent = dragPath.length === path.length && dragPath.slice(0, -1).every((v, i) => v === path[i]);
    if (!sameParent || pathKey(path) === pathKey(dragPath)) {
      setDragPath(null); setOverPath(null); setOverPos(null);
      return;
    }
    setItems(prev => {
      // remove dragged node
      const [removed, afterRemove] = removeByPath(prev, dragPath);
      if (!removed) return prev;
      // adjust target path index shift within same parent
      let targetPath = path.slice();
      const fromIdx = dragPath[dragPath.length - 1];
      const toIdx = path[path.length - 1];
      if (fromIdx < toIdx) {
        targetPath[targetPath.length - 1] = toIdx - 1;
      }
      const inserted = insertAtPath(afterRemove, targetPath, overPos || 'after', removed);
      return inserted;
    });
    setDragPath(null);
    setOverPath(null);
    setOverPos(null);
  };

  const onDragEnd = () => {
    setDragPath(null);
    setOverPath(null);
    setOverPos(null);
  };

  // Full URL slug (no leading/trailing slash), segments are lowercase alphanumeric with dashes
  const isValidSlug = (s) => /^[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*$/.test((s || '').trim());

  const save = async (e) => {
    e.preventDefault();
    // validation (recursive) and global unique slugs + max depth (2)
    const slugs = new Set();
    let invalid = '';
    const visit = (node, depth, rowLabel) => {
      if (depth > 2) { invalid = `${rowLabel}: nesting exceeds 2 levels`; return; }
      if (!node.name || !node.name.trim()) { invalid = `${rowLabel}: name is required`; return; }
      const slugVal = (node.slug || '').trim();
      const isTopLevel = depth === 1;
      // Allow empty slug for top-level (general/home) page, but require for deeper levels
      if (!slugVal) {
        if (!isTopLevel) { invalid = `${rowLabel}: slug is required`; return; }
      } else {
        if (!isValidSlug(slugVal)) { invalid = `${rowLabel}: slug must be lowercase alphanumeric words separated by dashes and /`; return; }
      }
      if (slugs.has(slugVal)) { invalid = `${rowLabel}: slug must be unique globally`; return; }
      slugs.add(slugVal);
      const children = node.children || [];
      for (let i = 0; i < children.length && !invalid; i++) {
        visit(children[i], depth + 1, `${rowLabel}.${i + 1}`);
      }
    };
    for (let i = 0; i < items.length && !invalid; i++) visit(items[i], 1, `Row ${i + 1}`);
    if (invalid) { toast.error(invalid); return; }
    try {
      setSaving(true);
      const payload = { menu: items };
      const resp = await patch(`websites/${website.id}/menu`, payload);
      const updated = resp?.website || resp || { ...website, menu: items };
      onUpdated?.(updated);
      setEdit(false);
    } catch (e) {
      toast.error('Failed to save menu.');
    } finally {
      setSaving(false);
    }
  };

  // Recursive renderer for edit mode
  const renderItem = (it, path, depth) => {
    const pk = pathKey(path);
    const isDragging = dragPath && pathKey(dragPath) === pk;
    const isOver = overPath && pathKey(overPath) === pk;
    const showTopLine = isOver && overPos === 'before' && !isDragging;
    const showBottomLine = isOver && overPos === 'after' && !isDragging;
    const showInsideHighlight = false; // no inside drops

    return (
      <div
        key={pk}
        onDragEnter={() => onDragEnter(path)}
        onDragOver={(e) => onDragOver(path, depth, e)}
        onDrop={(e) => onDrop(path, depth, e)}
        style={{
          border: '1px solid ' + (isOver ? '#3b82f6' : '#e5e7eb'),
          borderRadius: 10,
          padding: 12,
          paddingLeft: 36 + (depth - 1) * 16,
          marginBottom: 10,
          background: isDragging ? '#f8fafc' : (showInsideHighlight ? '#eef6ff' : 'white'),
          position: 'relative',
          boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.08)' : 'none',
          transform: isDragging ? 'scale(1.01)' : 'none',
          opacity: isDragging ? 0.92 : 1,
          transition: 'box-shadow 120ms ease, transform 120ms ease, opacity 120ms ease'
        }}
        ref={(el) => (itemRefs.current[pk] = el)}
      >
        {showTopLine && (
          <div style={{ position: 'absolute', top: -1, left: 0, right: 0, height: 2, background: '#3b82f6' }} />
        )}
        {showBottomLine && (
          <div style={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 2, background: '#3b82f6' }} />
        )}
        {/* Drag handle icon (left side, centered) */}
        <button
          type="button"
          aria-label="Drag to reorder/move"
          title="Drag to reorder/move"
          draggable
          onDragStart={(e) => onDragStart(path, e)}
          onDragEnd={onDragEnd}
          style={{
            position: 'absolute',
            top: '50%',
            left: (depth - 1) * 16,
            width: 24,
            height: 24,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            color: '#64748b',
            fontSize: 16,
            lineHeight: '16px',
            cursor: isDragging ? 'grabbing' : 'grab',
            transform: 'translateY(-50%)'
          }}
        >
          ⋮⋮
        </button>
        {/* Remove cross in top-right */}
        <button
          type="button"
          aria-label="Remove"
          onClick={() => {
            setItems(prev => {
              const [_, next] = removeByPath(prev, path);
              return next;
            });
          }}
          style={{
            position: 'absolute',
            top: '0',
            right: '0',
            width: 24,
            height: 24,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f65279',
            borderRadius: '0 .6rem',
            border: 'none',
            color: 'white',
            fontSize: '1rem',
            lineHeight: '18px',
            cursor: 'pointer'
          }}
          title="Remove"
        >
          ×
        </button>
        <div className="form-row-horizontal mb-2" style={{ marginTop: '1rem' }}>
          <Form.Label>Title</Form.Label>
          <Form.Control
            type="text"
            value={it.name || ''}
            onChange={(e) => updateFieldAtPath(path, 'name', e.target.value)}
            placeholder="e.g. About us"
          />
        </div>
        <div className="form-row-horizontal">
          <Form.Label>Path</Form.Label>
          <Form.Control
            type="text"
            value={it.slug || ''}
            onChange={(e) => updateFieldAtPath(path, 'slug', e.target.value)}
            placeholder={depth === 1 ? 'e.g. about-us' : 'e.g. parent/child'}
          />
        </div>
        {depth < 2 && (
          <div style={{ marginTop: 8 }}>
            <Button
              type="button"
              className='primary-button'
              size="sm"
              onClick={() => {
                setItems(prev => setByPath(prev, path, (node) => ({
                  ...node,
                  // Initialize child with parent's slug (no leading/trailing slash) so auto-completion works when typing title
                  children: [...(node.children || []), { name: '', slug: (node.slug ? `${(node.slug || '').replace(/^\/+|\/+$|/g, '')}` : ''), children: [] }]
                })));
              }}
            >
              Add sub-item
            </Button>
          </div>
        )}
        {/* Render children */}
        {(it.children || []).map((ch, idx) => renderItem(ch, [...path, idx], depth + 1))}
      </div>
    );
  };

  const renderViewItem = (it, depth) => (
    <li key={`${it.slug}-${depth}`} style={{ marginLeft: (depth - 1) * 16 }}>
      <strong>{it.name || '-'}</strong> — /{it.slug || ''}
      {(it.children || []).length > 0 && (
        <ul style={{ marginTop: 6 }}>
          {it.children.map(ch => renderViewItem(ch, depth + 1))}
        </ul>
      )}
    </li>
  );

  return (
    <div style={{ marginTop: '2rem' }}>
      {!hasWebsite ? (
        <Alert variant="warning">Website not found.</Alert>
      ) : !edit ? (
        <>
          <p style={{ marginBottom: '0.75rem', color: '#64748b' }}>Manage your navigation menu.</p>
          {(!Array.isArray(website?.menu?.items) || website?.menu?.items.length === 0) ? (
            <em>No menu items.</em>
          ) : (
            <ul style={{ marginBottom: 0 }}>
              {website?.menu?.items.map((it) => renderViewItem(it, 1))}
            </ul>
          )}
          <div style={{ marginTop: '12px' }}>
            <Button className="login-button" size="sm" onClick={startEdit}>Edit</Button>
          </div>
        </>
      ) : (
        <>
          <Form onSubmit={save}>
            {items.map((it, idx) => renderItem(it, [idx], 1))}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <Button type="button" className='primary-button' size="sm" onClick={addItem}>Add item</Button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="submit" className="primary-button" disabled={saving}>
                {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
              </Button>
              <Button type="button" className='primary-button' onClick={cancelEdit} disabled={saving}>Cancel</Button>
            </div>
          </Form>
        </>
      )}
    </div>
  );
}

export default Menu;
